import { Note, Review } from '../types/items';
import { CareerCard } from '../types/careerCard';
import { createLogger } from './logger';

const logger = createLogger({ module: 'changeDetection', level: 'info' });

// Type guards
function isNote(item: Note | Review | CareerCard): item is Note {
  return 'type' in item && item.type === 'note';
}

function isReview(item: Note | Review | CareerCard): item is Review {
  return 'type' in item && item.type === 'review';
}

function isCareerCard(item: Note | Review | CareerCard): item is CareerCard {
  return 'card_data' in item;
}

export function hasContentChanged(original: Note | Review | CareerCard, current: Note | Review | CareerCard): boolean {
  logger.debug('Checking content changes', {
    originalType: original.type,
    currentType: current.type
  });

  // First check if types match
  if (isNote(original) && isNote(current)) {
    return hasNoteChanged(original, current);
  }
  
  if (isReview(original) && isReview(current)) {
    return hasReviewChanged(original, current);
  }
  
  if (isCareerCard(original) && isCareerCard(current)) {
    return hasCareerCardChanged(original, current);
  }
  
  // If types don't match, consider it changed
  return true;
}

function hasNoteChanged(original: Note, current: Note): boolean {
  const changes = {
    titleChanged: original.title !== current.title,
    contentChanged: original.content !== current.content,
    impactChanged: original.career_impact !== current.career_impact,
    engagementChanged: original.engagement_level !== current.engagement_level,
    tagsChanged: !arraysEqual(original.custom_tags || [], current.custom_tags || []),
    noteDateChanged: original.note_date !== current.note_date
  };

  logger.debug('Checking note changes', {
    ...changes,
    originalNoteDate: original.note_date,
    currentNoteDate: current.note_date
  });

  return Object.values(changes).some(changed => changed);
}

function hasReviewChanged(original: Review, current: Review): boolean {
  const changes = {
    titleChanged: original.title !== current.title,
    contentChanged: original.content !== current.content,
    tagsChanged: !arraysEqual(original.custom_tags || [], current.custom_tags || []),
    careerCardIdsChanged: !arraysEqual(original.career_card_ids || [], current.career_card_ids || []),
    noteIdsChanged: !arraysEqual(original.note_ids || [], current.note_ids || []),
    reviewDateChanged: original.review_date !== current.review_date
  };

  logger.debug('Checking review changes', {
    ...changes,
    originalTitle: original.title,
    currentTitle: current.title,
    originalContent: original.content,
    currentContent: current.content,
    originalTags: original.custom_tags,
    currentTags: current.custom_tags,
    originalReviewDate: original.review_date,
    currentReviewDate: current.review_date
  });

  return Object.values(changes).some(changed => changed);
}

function hasCareerCardChanged(original: CareerCard, current: CareerCard): boolean {
  // Check basic fields
  if (
    original.career_impact.value !== current.career_impact.value ||
    original.engagement_level.value !== current.engagement_level.value
  ) {
    return true;
  }

  // Check card_data fields
  const originalData = original.card_data;
  const currentData = current.card_data;

  // Check title and type
  if (
    originalData.title !== currentData.title ||
    originalData.type !== currentData.type
  ) {
    return true;
  }

  // Check arrays
  if (
    !arraysEqual(originalData.custom_tags || [], currentData.custom_tags || []) ||
    !arraysEqual(originalData.date_tags || [], currentData.date_tags || [])
  ) {
    return true;
  }

  // Deep compare the rest of card_data
  // Exclude known fields we've already checked and fields we want to ignore
  const excludeFields = new Set(['title', 'type', 'custom_tags', 'date_tags', 'impact_history']);
  const remainingFields = Object.keys(originalData).filter(key => !excludeFields.has(key));

  return remainingFields.some(field => {
    const originalValue = originalData[field];
    const currentValue = currentData[field];

    // For objects and arrays, do a deep comparison
    if (
      typeof originalValue === 'object' ||
      Array.isArray(originalValue)
    ) {
      return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
    }

    // For primitive values, do a direct comparison
    return originalValue !== currentValue;
  });
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
} 